import React, { useState } from 'react'
import {
  Globe,
  Plus,
  Eye,
  Edit,
  ExternalLink,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Grid3X3,
  List
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { useHasPermission } from '../../hooks/usePermissions'

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'published': return 'badge badge-success'
    case 'draft':     return 'badge badge-warning'
    default:          return 'badge'
  }
}

const getStatusIcon = (status) => {
  switch (status) {
    case 'published': return CheckCircle
    case 'archived':  return AlertCircle
    default:          return Clock
  }
}

// Gradient colors for site avatars (inline styles since Tailwind is removed)
const BANNER_GRADIENTS = [
  ['#8b5cf6', '#6d28d9'],
  ['#6c63ff', '#5940e6'],
  ['#f43f5e', '#ec4899'],
  ['#10b981', '#0891b2'],
  ['#f97316', '#f59e0b'],
  ['#3b82f6', '#6366f1'],
  ['#ec4899', '#f43f5e'],
  ['#14b8a6', '#06b6d4'],
]

const getBannerGradient = (name = '') => {
  const index = (name.charCodeAt(0) || 0) % BANNER_GRADIENTS.length
  return BANNER_GRADIENTS[index]
}

const getLiveUrl = (site) => {
  if (site.custom_domain) {
    return `${site.custom_domain_ssl ? 'https' : 'http'}://${site.custom_domain}`
  }
  if (site.azure_published_url) return site.azure_published_url
  const clientSlug = site.company_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'unknown'
  return `/sites/${clientSlug}/${site.slug}/`
}

const getRelativeTime = (timestamp) => {
  const diff = Date.now() - new Date(timestamp)
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (m < 1)  return 'Just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 7)  return `${d}d ago`
  return new Date(timestamp).toLocaleDateString('en-GB')
}

const getSiteEditorLogo = (site) => {
  try {
    const meta = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : (site.meta_data || {})
    return meta.logo || null
  } catch {
    return null
  }
}

// ── Site Card (grid view) ──────────────────────────────────────────────────
const SiteCard = ({ site, canEditText, canDeleteSites, onDelete }) => {
  const StatusIcon = getStatusIcon(site.status)
  const initial = site.name?.charAt(0).toUpperCase() || '?'
  const isPublished = site.status === 'published'
  const [gradStart, gradEnd] = getBannerGradient(site.name)
  const logoUrl = getSiteEditorLogo(site)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02, transition: { duration: 0.18 } }}
    >
      <div className="card overflow-hidden" style={{ transition: 'box-shadow 0.2s' }}>
        {/* Gradient top border */}
        <div style={{ height: '0.5rem', background: `linear-gradient(to right, ${gradStart}, ${gradEnd})` }} />

        <div style={{ padding: '1rem' }}>
          {/* Logo + name row */}
          <div className="flex items-center gap-3" style={{ marginBottom: '0.75rem' }}>
            {logoUrl ? (
              <div style={{ width: '3rem', height: '3rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                <img
                  src={logoUrl}
                  alt={site.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '0.375rem' }}
                  onError={(e) => {
                    e.currentTarget.parentElement.innerHTML = `<span style="font-size:1rem;font-weight:700;color:white;width:100%;height:100%;display:flex;align-items:center;justify-content:center;border-radius:var(--radius-lg);background:linear-gradient(135deg,${gradStart},${gradEnd})">${initial}</span>`
                  }}
                />
              </div>
            ) : (
              <div style={{ width: '3rem', height: '3rem', borderRadius: 'var(--radius-lg)', background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: 'white', userSelect: 'none' }}>{initial}</span>
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 className="font-semibold truncate" style={{ color: 'var(--text)', lineHeight: 1.3, margin: 0 }}>{site.name}</h3>
              <span className={getStatusBadgeClass(site.status)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                <StatusIcon style={{ width: '0.75rem', height: '0.75rem' }} />
                {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
              </span>
            </div>
            {canDeleteSites && (
              <button
                onClick={() => onDelete(site)}
                title="Delete site"
                style={{ padding: '0.375rem', borderRadius: 'var(--radius-sm)', color: 'var(--text-faint)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
              >
                <Trash2 style={{ width: '0.875rem', height: '0.875rem' }} />
              </button>
            )}
          </div>

          {/* Quick-stats row */}
          <div className="flex items-center gap-3 text-faint" style={{ fontSize: '0.75rem', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid var(--border)' }}>
            <span className="flex items-center gap-1" style={{ flexShrink: 0 }}>
              <Clock style={{ width: '0.75rem', height: '0.75rem' }} />
              {getRelativeTime(site.updated_at)}
            </span>
            <span className="flex items-center gap-1" style={{ minWidth: 0 }}>
              {site.custom_domain ? (
                <>
                  <Globe style={{ width: '0.75rem', height: '0.75rem', color: 'var(--success)', flexShrink: 0 }} />
                  <span className="truncate" style={{ color: 'var(--success)' }}>{site.custom_domain}</span>
                </>
              ) : (
                <>
                  <Globe style={{ width: '0.75rem', height: '0.75rem' }} />
                  <span>No custom domain</span>
                </>
              )}
            </span>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {canEditText && (
              <Link to={`/client/editor?site=${site.id}`} className="btn btn-primary btn-sm w-full" style={{ justifyContent: 'center' }}>
                <Edit style={{ width: '0.875rem', height: '0.875rem' }} />
                Edit Site
              </Link>
            )}
            {isPublished ? (
              <button
                className="btn btn-ghost btn-sm w-full"
                onClick={() => window.open(getLiveUrl(site), '_blank')}
              >
                <ExternalLink style={{ width: '0.875rem', height: '0.875rem' }} />
                Visit Live Site
              </button>
            ) : (
              <Link to={`/client/preview/${site.id}`} className="btn btn-ghost btn-sm w-full" style={{ justifyContent: 'center' }}>
                <Eye style={{ width: '0.875rem', height: '0.875rem' }} />
                Preview
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// ── Site Row (list view) ───────────────────────────────────────────────────
const SiteRow = ({ site, canEditText, canDeleteSites, onDelete }) => {
  const StatusIcon = getStatusIcon(site.status)
  const isPublished = site.status === 'published'
  const [gradStart, gradEnd] = getBannerGradient(site.name)
  const initial = site.name?.charAt(0).toUpperCase() || '?'
  const logoUrl = getSiteEditorLogo(site)

  return (
    <tr style={{ transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <td style={{ padding: '1rem 1.5rem' }}>
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
              <img src={logoUrl} alt={site.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '0.25rem' }} />
            </div>
          ) : (
            <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: 'var(--radius-lg)', background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'white' }}>{initial}</span>
            </div>
          )}
          <div>
            <div className="font-medium" style={{ color: 'var(--text)' }}>{site.name}</div>
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              {site.custom_domain ? (
                <span className="flex items-center gap-1">
                  <Globe style={{ width: '0.75rem', height: '0.75rem' }} />
                  {site.custom_domain_ssl ? 'https' : 'http'}://{site.custom_domain}
                </span>
              ) : `/${site.slug}`}
            </div>
          </div>
        </div>
      </td>
      <td style={{ padding: '1rem 1.5rem' }}>
        <span className={getStatusBadgeClass(site.status)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
          <StatusIcon style={{ width: '0.75rem', height: '0.75rem' }} />
          {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
        </span>
      </td>
      <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', color: 'var(--text-soft)' }}>
        {site.custom_domain ? (
          <span className="flex items-center gap-1">
            <Globe style={{ width: '0.75rem', height: '0.75rem', color: 'var(--success)' }} />
            <span style={{ color: 'var(--success)' }}>{site.custom_domain}</span>
            {site.custom_domain_ssl && <CheckCircle style={{ width: '0.75rem', height: '0.75rem', color: 'var(--success)' }} />}
          </span>
        ) : (
          <span className="text-faint">No custom domain</span>
        )}
      </td>
      <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', color: 'var(--text-soft)' }}>
        <span className="flex items-center gap-1">
          <Clock style={{ width: '0.75rem', height: '0.75rem', color: 'var(--text-faint)' }} />
          {getRelativeTime(site.updated_at)}
        </span>
      </td>
      <td style={{ padding: '1rem 1.5rem' }}>
        <div className="flex gap-2">
          {canEditText && (
            <Link to={`/client/editor?site=${site.id}`} className="btn btn-ghost btn-icon btn-sm" title="Edit">
              <Edit style={{ width: '1rem', height: '1rem' }} />
            </Link>
          )}
          {isPublished ? (
            <button className="btn btn-ghost btn-icon btn-sm" title="Visit live site" onClick={() => window.open(getLiveUrl(site), '_blank')}>
              <ExternalLink style={{ width: '1rem', height: '1rem' }} />
            </button>
          ) : (
            <Link to={`/client/preview/${site.id}`} className="btn btn-ghost btn-icon btn-sm" title="Preview">
              <Eye style={{ width: '1rem', height: '1rem' }} />
            </Link>
          )}
          {canDeleteSites && (
            <button
              className="btn btn-ghost btn-icon btn-sm"
              title="Delete site"
              onClick={() => onDelete(site)}
              style={{ color: 'var(--danger)' }}
            >
              <Trash2 style={{ width: '1rem', height: '1rem' }} />
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── Main component ─────────────────────────────────────────────────────────
const ClientSites = () => {
  const [view, setView] = useState('grid')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [siteToDelete, setSiteToDelete] = useState(null)
  const canEditText = useHasPermission('can_edit_text')
  const canDeleteSites = useHasPermission('can_delete_sites')
  const queryClient = useQueryClient()

  const { data: sites, isLoading } = useQuery(
    'client-sites',
    () => apiService.get('/sites').then(r => r.data.sites || []),
    { staleTime: 30000 }
  )

  const deleteSiteMutation = useMutation(
    (siteId) => apiService.delete(`/sites/${siteId}`),
    {
      onSuccess: () => {
        toast.success('Site deleted successfully!')
        queryClient.invalidateQueries(['client-sites'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete site')
      }
    }
  )

  const handleDeleteSite = (site) => {
    setSiteToDelete(site)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (siteToDelete) {
      deleteSiteMutation.mutate(siteToDelete.id)
      setShowDeleteConfirm(false)
      setSiteToDelete(null)
    }
  }

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeleteConfirm && !event.target.closest('.modal-content')) {
        setShowDeleteConfirm(false)
        setSiteToDelete(null)
      }
    }
    if (showDeleteConfirm) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDeleteConfirm])

  const displaySites = Array.isArray(sites) ? sites : []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '16rem' }}>
        <LoadingSpinner size="lg" text="Loading sites..." />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>My Sites</h1>
          <p className="text-soft" style={{ marginTop: '0.25rem' }}>Manage and create your websites</p>
        </div>

        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div style={{ display: 'flex', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden' }}>
            <button
              onClick={() => setView('grid')}
              className={view === 'grid' ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              style={{ borderRadius: 0, gap: '0.25rem' }}
            >
              <Grid3X3 style={{ width: '1rem', height: '1rem' }} />
              Grid
            </button>
            <button
              onClick={() => setView('list')}
              className={view === 'list' ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              style={{ borderRadius: 0, gap: '0.25rem' }}
            >
              <List style={{ width: '1rem', height: '1rem' }} />
              List
            </button>
          </div>

          <Link to="/client/sites/new" className="btn btn-primary">
            <Plus style={{ width: '1rem', height: '1rem' }} />
            Create Site
          </Link>
        </div>
      </div>

      {/* Sites content */}
      {displaySites.length > 0 ? (
        view === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.5rem' }}>
            {displaySites.map((site) => (
              <SiteCard
                key={site.id}
                site={site}
                canEditText={canEditText}
                canDeleteSites={canDeleteSites}
                onDelete={handleDeleteSite}
              />
            ))}
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--surface-2)' }}>
                  <tr>
                    {['Site', 'Status', 'Domain', 'Updated', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '0.75rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ borderTop: '1px solid var(--border)' }}>
                  {displaySites.map((site, i) => (
                    <React.Fragment key={site.id}>
                      {i > 0 && <tr><td colSpan={5} style={{ borderTop: '1px solid var(--border)', padding: 0 }} /></tr>}
                      <SiteRow
                        site={site}
                        canEditText={canEditText}
                        canDeleteSites={canDeleteSites}
                        onDelete={handleDeleteSite}
                      />
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="card card-pad" style={{ textAlign: 'center' }}>
          <Globe style={{ width: '4rem', height: '4rem', color: 'var(--text-faint)', margin: '0 auto 1rem' }} />
          <h3 className="font-medium" style={{ fontSize: '1.125rem', color: 'var(--text)', marginBottom: '0.5rem' }}>No sites yet</h3>
          <p className="text-muted" style={{ marginBottom: '1.5rem' }}>Get started by creating your first website</p>
          <Link to="/client/sites/new" className="btn btn-primary" style={{ display: 'inline-flex' }}>
            <Plus style={{ width: '1rem', height: '1rem' }} />
            Create Your First Site
          </Link>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && siteToDelete && (
        <div className="modal-backdrop">
          <div className="modal modal-content" style={{ maxWidth: '28rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '3rem', height: '3rem', margin: '0 auto 1rem', background: 'var(--danger-soft)', borderRadius: '50%' }}>
              <Trash2 style={{ width: '1.5rem', height: '1.5rem', color: 'var(--danger)' }} />
            </div>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h3 className="font-semibold" style={{ fontSize: '1.125rem', color: 'var(--text)', marginBottom: '0.5rem' }}>Delete Site</h3>
              <p className="text-soft">
                Are you sure you want to delete <span className="font-medium" style={{ color: 'var(--text)' }}>"{siteToDelete.name}"</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                className="btn btn-ghost"
                onClick={() => { setShowDeleteConfirm(false); setSiteToDelete(null) }}
                disabled={deleteSiteMutation.isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleteSiteMutation.isLoading}
              >
                {deleteSiteMutation.isLoading ? (
                  <><LoadingSpinner size="sm" />Deleting...</>
                ) : (
                  <><Trash2 style={{ width: '1rem', height: '1rem' }} />Delete Site</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientSites
